/* Общие утилиты: время, форматирование, телефоны, ГСЧ. Без зависимостей от DOM — работают и в браузере, и на сервере.
   Календарные функции используют локальную таймзону процесса: в браузере — устройства, на сервере — переменная TZ. */

export const MIN = 60 * 1000;
export const HOUR = 60 * MIN;
export const DAY = 24 * HOUR;

export function now(): number {
  return Date.now();
}

export function startOfDay(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function at(dayTs: number, h: number, m = 0): number {
  const d = new Date(dayTs);
  d.setHours(h, m, 0, 0);
  return d.getTime();
}

const nf = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });

let currencySign = "₸";

/** Знак валюты точки (₸, сум, сом, ₽). Меняется при переключении точки другой страны. */
export function setCurrencySign(sign: string): void {
  currencySign = sign;
}

export function money(v: number | null | undefined): string {
  return nf.format(Math.round(v || 0)) + " " + currencySign;
}

export function moneyShort(v: number): string {
  const a = Math.abs(v);
  if (a >= 1e6) return (v / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace(".", ",") + " млн " + currencySign;
  if (a >= 1e4) return Math.round(v / 1e3) + " тыс " + currencySign;
  return money(v);
}

export function num(v: number | null | undefined): string {
  return nf.format(Math.round(v || 0));
}

export function pct(v: number, digits = 0): string {
  return (v * 100).toFixed(digits).replace(".", ",") + " %";
}

export function pad(n: number): string {
  return String(n).padStart(2, "0");
}

export function time(ts: number): string {
  const d = new Date(ts);
  return pad(d.getHours()) + ":" + pad(d.getMinutes());
}

export const MONTHS = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];
export const MONTHS_SHORT = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
export const WEEKDAYS = ["воскресенье", "понедельник", "вторник", "среда", "четверг", "пятница", "суббота"];
export const WD_SHORT = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];

export function dateLong(ts: number): string {
  const d = new Date(ts);
  return d.getDate() + " " + MONTHS[d.getMonth()] + ", " + WEEKDAYS[d.getDay()];
}

export function dateShort(ts: number): string {
  const d = new Date(ts);
  return d.getDate() + " " + MONTHS_SHORT[d.getMonth()];
}

export function dayLabel(ts: number): string {
  const diff = Math.round((startOfDay(ts) - startOfDay(now())) / DAY);
  if (diff === 0) return "Сегодня";
  if (diff === 1) return "Завтра";
  if (diff === -1) return "Вчера";
  return dateShort(ts);
}

export function dateTime(ts: number): string {
  return dayLabel(ts) + ", " + time(ts);
}

export function duration(min: number): string {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (!h) return m + " мин";
  if (!m) return h + " ч";
  return h + " ч " + m + " мин";
}

export function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

export function ago(ts: number): string {
  const diff = Math.round((now() - ts) / MIN);
  if (diff < 1) return "только что";
  if (diff < 60) return diff + " мин назад";
  if (diff < 60 * 24) return Math.floor(diff / 60) + " ч назад";
  const days = Math.floor(diff / 60 / 24);
  return days + " " + plural(days, "день", "дня", "дней") + " назад";
}

export function initials(name: string): string {
  return String(name)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

/** Телефон в формате E.164 без «+»: 77011234567. Поддерживает 8XXXXXXXXXX и 10-значные номера Казахстана. */
export function phoneNorm(p: string | number | null | undefined): string {
  let d = String(p ?? "").replace(/\D/g, "");
  if (d.length === 10) d = "7" + d;
  if (d.length === 11 && d[0] === "8") d = "7" + d.slice(1);
  return d;
}

export function phoneFmt(p: string): string {
  const d = String(p).replace(/\D/g, "");
  if (d.length !== 11) return p;
  return "+7 " + d.slice(1, 4) + " " + d.slice(4, 7) + " " + d.slice(7, 9) + " " + d.slice(9, 11);
}

/** Госномер без пробелов в верхнем регистре — для поиска и дедупликации. */
export function plateNorm(p: string | null | undefined): string {
  return String(p ?? "").replace(/\s+/g, "").toUpperCase();
}

export interface Rng {
  (): number;
  int(min: number, max: number): number;
  pick<T>(arr: readonly T[]): T;
  chance(p: number): boolean;
}

/** Детерминированный ГСЧ (mulberry32) — для демо-данных. */
export function rng(seed: number): Rng {
  let a = seed >>> 0;
  const next = function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  } as Rng;
  next.int = (min, max) => Math.floor(next() * (max - min + 1)) + min;
  next.pick = (arr) => arr[Math.floor(next() * arr.length)];
  next.chance = (p) => next() < p;
  return next;
}

export function round(v: number, step: number): number {
  return Math.round(v / step) * step;
}

export function clamp(v: number, a: number, b: number): number {
  return Math.max(a, Math.min(b, v));
}

export function sum<T>(arr: readonly T[], fn?: (x: T) => number): number {
  return arr.reduce((s, x) => s + (fn ? fn(x) : (x as unknown as number)), 0);
}

export function groupBy<T>(arr: readonly T[], fn: (x: T) => string | number): Record<string, T[]> {
  const out: Record<string, T[]> = {};
  for (const x of arr) {
    const k = String(fn(x));
    (out[k] = out[k] || []).push(x);
  }
  return out;
}
